import { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw, Archive, Rocket } from "lucide-react";

/**
 * Marketplace Administration · /admin/marketplace
 * Super-admin controls governing Demo Mode / Production Go-Live.
 */
export default function MarketplaceAdmin() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/marketplace-admin/settings"); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key, value) => {
    try {
      await api.patch("/marketplace-admin/settings", { [key]: value });
      toast.success(`${key} → ${value}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const archive = async () => {
    if (!confirm(`Archive ${data?.counts?.demo_listings || 0} demo listings? They will be moved to vehicles_demo_archive.`)) return;
    try { const r = await api.post("/marketplace-admin/archive-demo"); toast.success(`Archived ${r.data.archived}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const goLive = async () => {
    if (!confirm("PROMOTE TO LIVE?\n\nThis disables Demo Mode and hides demo listings from public users. Only genuine verified assets will appear in the public marketplace. Demo data remains in the database for internal use.")) return;
    try { const r = await api.post("/marketplace-admin/promote-to-live"); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const s = data?.settings;
  const c = data?.counts;
  return (
    <div>
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <header className="mb-8">
          <div className="kicker">Executive · Marketplace Administration</div>
          <h1 className="display text-4xl">Demo Mode · Production Go-Live</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Govern demonstration content across the entire marketplace. Toggle Demo Mode, hide demo listings from public users, archive demonstration data, or promote the platform to live mode — all without a code change.
          </p>
        </header>

        {/* Counts */}
        <div className="grid md:grid-cols-3 gap-4 mb-8" data-testid="mpadmin-counts">
          <K label="Demo listings"       value={c?.demo_listings} tone="amber" />
          <K label="Live listings"       value={c?.live_listings} tone="emerald" />
          <K label="Total"               value={c?.total} tone="neutral" />
        </div>

        {/* Settings */}
        <div className="border border-border bg-card divide-y divide-border" data-testid="mpadmin-toggles">
          <Row label="Demo Mode enabled"
                desc="When off, no new demo listings can be created and existing demo listings are hidden from all public queries."
                on={!!s?.demo_mode} onChange={(v) => toggle("demo_mode", v)} testid="toggle-demo-mode" />
          <Row label="Hide demo from public"
                desc="When on, demo listings are filtered out of the public marketplace. Administrators can still view them via the 'Demo Listings (Admin)' filter."
                on={!!s?.hide_demo_from_public} onChange={(v) => toggle("hide_demo_from_public", v)} testid="toggle-hide-demo" />
          <Row label="Demonstration banner"
                desc="Show the amber 'Demonstration Marketplace' banner at the top of any page that surfaces demo listings."
                on={!!s?.banner_enabled} onChange={(v) => toggle("banner_enabled", v)} testid="toggle-banner" />
          <Row label="Demo watermark on images"
                desc="Render a diagonal 'DEMONSTRATION' watermark on every demo listing image."
                on={!!s?.watermark_enabled} onChange={(v) => toggle("watermark_enabled", v)} testid="toggle-watermark" />
        </div>

        {/* Actions */}
        <div className="mt-8 space-y-3">
          <div className="border border-border bg-card p-4 flex items-start gap-3">
            <Archive className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-[14px]">Archive demonstration listings</div>
              <div className="text-[12.5px] text-muted-foreground">Move every current demo vehicle out of the live marketplace collection into <code>vehicles_demo_archive</code>. Preserves the data for internal training/testing.</div>
            </div>
            <Button variant="outline" onClick={archive} disabled={busy || !c?.demo_listings} data-testid="mpadmin-archive">Archive {c?.demo_listings || 0}</Button>
          </div>
          <div className="border-2 border-amber-500 bg-amber-50 p-4 flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-amber-700 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-[14px] text-amber-900">Production Go-Live</div>
              <div className="text-[12.5px] text-amber-900/90">Disables Demo Mode, hides demo listings from public users, turns off banner + watermark. Only genuine verified assets will appear in the public marketplace. This is the single click that flips THOM Verify™ from demonstration to production.</div>
            </div>
            <Button onClick={goLive} disabled={busy} data-testid="mpadmin-golive"><Rocket className="h-3.5 w-3.5 mr-1" /> Promote to LIVE</Button>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
          <Button size="sm" variant="outline" onClick={load} disabled={busy}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
          Last updated: {s?.updated_at?.slice(0, 19).replace("T", " ") || "—"}{s?.updated_by ? ` · ${s.updated_by}` : ""}
        </div>
      </div>
      <Footer />
    </div>
  );
}

const K = ({ label, value, tone }) => {
  const T = { emerald: "bg-emerald-500/5 border-emerald-500/20 text-emerald-700",
              amber:   "bg-amber-500/5 border-amber-500/20 text-amber-700",
              neutral: "bg-card border-border" }[tone] || "";
  return (
    <div className={`border p-4 ${T}`}>
      <div className="mono text-[9.5px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-3xl font-semibold mt-1">{value ?? "—"}</div>
    </div>
  );
};

const Row = ({ label, desc, on, onChange, testid }) => (
  <div className="p-4 flex items-start gap-4">
    <div className="flex-1">
      <div className="font-semibold text-[13.5px]">{label}</div>
      <div className="text-[12px] text-muted-foreground mt-0.5">{desc}</div>
    </div>
    <Switch checked={on} onCheckedChange={onChange} data-testid={testid} />
  </div>
);
